import { Autocomplete, ChipProps as ChipPropsType, TextField } from '@mui/material';
import {
  CuriosityTranslationOrKey,
  isCuriosityTranslation,
  useCuriosityAutocomplete,
  CuriosityAutocompleteProps,
  useValueSimplify,
} from '@procureai/curiosity-ui-mui';
import { forwardRef, ForwardedRef } from 'react';
import { useTranslation } from 'react-i18next';

import { createStaticHit, useUserAutocomplete } from './UserAutocomplete.hooks';
import { UserEntity, UserHitId } from './UserAutocomplete.types';
import { UserOption } from './UserOption/UserOption';

const ns = 'user';
const ChipProps: ChipPropsType = { variant: 'outlined' };

type FreeSolo = false;

export interface UserAutocompleteProps<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined = false,
> extends Omit<CuriosityAutocompleteProps<UserEntity, Multiple, DisableClearable>, 'ns' | 'translateSentinel'> {
  hasEmptyOption?: boolean;
  translationPrefix?: string;
}

const UserAutocompleteInner = <Multiple extends boolean | undefined, DisableClearable extends boolean | undefined>(
  {
    TextFieldProps,
    onChange: onChangeProp,
    value: valueProp,
    size = 'small',
    multiple,
    simplifyValue,
    hasEmptyOption,
    translationPrefix,
    ...props
  }: UserAutocompleteProps<Multiple, DisableClearable>,
  ref: ForwardedRef<unknown>,
) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Autocomplete' });
  const { t: tUserAutocomplete } = useTranslation(undefined, { keyPrefix: translationPrefix ?? 'UserAutocomplete' });
  const { value, onChange } = useValueSimplify({ simplifyValue, onChange: onChangeProp, value: valueProp, multiple });
  const autocompleteProps = useCuriosityAutocomplete<UserEntity, Multiple, DisableClearable>({
    ns,
    multiple,
    value,
    onChange,
    translateSentinel: (value) => {
      if (value === UserHitId.EMPTY) {
        return createStaticHit(UserHitId.EMPTY, tUserAutocomplete(UserHitId.EMPTY));
      }
    },
  });
  const additionalProps = useUserAutocomplete({
    hasEmptyOption,
    translationPrefix,
    ...autocompleteProps,
  });

  return (
    <Autocomplete<CuriosityTranslationOrKey<UserEntity>, Multiple, DisableClearable, FreeSolo>
      ref={ref}
      size="small"
      openOnFocus
      filterSelectedOptions
      noOptionsText={t('noOptions')}
      loadingText={t('loading')}
      renderInput={(params) => <TextField {...params} {...TextFieldProps} />}
      renderOption={(params, option) => {
        return isCuriosityTranslation(option) ? (
          <UserOption {...params} data={option} key={(option as any).key} />
        ) : null;
      }}
      {...props}
      {...additionalProps}
      slotProps={{
        chip: ChipProps,
      }}
    />
  );
};

export const UserAutocomplete = forwardRef(UserAutocompleteInner) as <
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
>(
  props: UserAutocompleteProps<Multiple, DisableClearable> & {
    ref?: ForwardedRef<unknown>;
  },
) => ReturnType<typeof UserAutocompleteInner>;
